import React, { memo, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import localforage from "localforage";
import { Box, Button, List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Sync, Analytics, Portrait, SpeakerNotesOutlined, WarningAmber, Tag } from "@mui/icons-material";
import clsx from "clsx";
import { AppConstant, ImageConstant, KeyConstant, LangConstant, PathConstant } from "const";
import { AdvancedConfirmDialog } from "components";
import { AuthUtils, EventBusUtils, StorageUtils } from "utils";
import { useRole } from "hooks";

const Sidebar = () => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const { t: getLabel } = useTranslation(LangConstant.NS_COMMON);

  const sidebarTitles = getLabel(LangConstant.OBJ_SIDEBAR_TITLE, { returnObjects: true });

  const { isServiceAdmin } = useRole();

  const [isConfirmationDialog, setIsConfirmationDialog] = useState(false);

  const handleCloseConfirmationDialog = () => {
    setIsConfirmationDialog(false);
  };

  const [branchAvatar, setBranchAvatar] = useState("");

  const sidebarItems = [
    {
      href: PathConstant.COMPANY_MANAGEMENT,
      icon: ImageConstant.CompanyManagementIcon,
      title: sidebarTitles.companyManagement,
    },
    {
      href: PathConstant.POSITION_MANAGEMENT,
      icon: Portrait,
      title: sidebarTitles.positionManagement,
    },
    {
      href: PathConstant.USER_MANAGEMENT,
      icon: ImageConstant.UserManagementIcon,
      title: sidebarTitles.userManagement,
    },
    {
      href: PathConstant.MEDIA_MANAGEMENT,
      icon: SpeakerNotesOutlined,
      title: sidebarTitles.mediaManagement,
    },
    {
      href: PathConstant.CHANNEL_MANAGEMENT,
      icon: Tag,
      title: sidebarTitles.channelManagement,
    },
    {
      href: PathConstant.STATISTIC,
      icon: Analytics,
      title: sidebarTitles.statistic,
    },
    {
      href: PathConstant.LICENSE,
      icon: ImageConstant.LicenseIcon,
      title: sidebarTitles.license,
    },
    {
      href: PathConstant.SYSTEM_TRACKING,
      icon: WarningAmber,
      title: sidebarTitles.systemTracking,
    },
    {
      href: PathConstant.SETTINGS,
      icon: ImageConstant.SettingIcon,
      title: sidebarTitles.settings,
    },
    {
      href: PathConstant.CONFERENCE_MANAGEMENT,
      icon: ImageConstant.ApplicationIcon,
      title: sidebarTitles.conferenceManagement,
    },
    {
      href: PathConstant.SERVER_MONITORING,
      icon: ImageConstant.MonitoringIcon,
      title: sidebarTitles.serverMonitoring,
    },
  ];

  const filteredSidebarItems = isServiceAdmin
    ? sidebarItems.filter(item =>
        [
          PathConstant.COMPANY_MANAGEMENT,
          PathConstant.POSITION_MANAGEMENT,
          PathConstant.USER_MANAGEMENT,
          PathConstant.SETTINGS,
          PathConstant.CONFERENCE_MANAGEMENT,
        ].includes(item.href),
      )
    : sidebarItems;

  const redirect = href => {
    if (href) history.push(href);
  };

  const handleGetBranchAvatar = avatar => {
    setBranchAvatar(avatar);
  };

  useEffect(() => {
    EventBusUtils.on(AppConstant.EVENT_BUS_EVENTS.fetchBranchDetails, handleGetBranchAvatar);
    return () => EventBusUtils.off(AppConstant.EVENT_BUS_EVENTS.fetchBranchDetails, handleGetBranchAvatar);
  });

  useEffect(() => {
    const branchAttachmentId = StorageUtils.getStoreData(KeyConstant.KEY_BRANCH_ATTACHMENT);
    localforage.getItem(KeyConstant.KEY_CACHED_BRANCH_AVATAR_BLOB + branchAttachmentId).then(avatar => {
      if (avatar) setBranchAvatar(URL.createObjectURL(avatar));
    });
  }, []);

  return (
    <Box>
      <Box className={classes.triosLogoContainer}>
        <Box
          component="img"
          className={clsx(classes.triosLogo, {
            [classes.triosLogoRounded]: !isServiceAdmin,
          })}
          src={branchAvatar || window.env.URL_DEFAULT_AVATAR || ImageConstant.TriosLogo}
        />
      </Box>
      <List className={classes.sidebarContainer}>
        {filteredSidebarItems.map((sidebar, index) => {
          const Icon = sidebar.icon;
          return (
            <ListItemButton
              key={index}
              className={classes.sidebarItem}
              selected={location.pathname === sidebar.href}
              onClick={() => redirect(sidebar.href)}
              disableTouchRipple
            >
              <ListItemIcon className={classes.sidebarIcon}>
                <Icon
                  className={clsx({
                    [classes.sidebarIconActive]: location.pathname === sidebar.href,
                  })}
                />
              </ListItemIcon>
              <ListItemText
                classes={{
                  primary: location.pathname === sidebar.href ? classes.sidebarTitleActive : classes.sidebarTitle,
                }}
                primary={sidebar.title}
              />
            </ListItemButton>
          );
        })}
        <ListItemButton
          className={classes.sidebarItem}
          onClick={() => setIsConfirmationDialog(true)}
          disableTouchRipple
        >
          <ListItemIcon className={classes.sidebarIcon}>
            <ImageConstant.LogoutIcon />
          </ListItemIcon>
          <ListItemText
            classes={{
              primary: classes.sidebarTitle,
            }}
            primary={sidebarTitles.logout}
          />
        </ListItemButton>
      </List>
      {!isServiceAdmin && (
        <Box className={classes.footer}>
          <Box className={classes.switchBranchBox}>
            <Button
              variant="contained"
              className={classes.switchButton}
              startIcon={<Sync />}
              href={PathConstant.BRANCH_MANAGEMENT}
            >
              {getLabel(LangConstant.L_SWITCH_BRANCH)}
            </Button>
          </Box>
        </Box>
      )}
      <AdvancedConfirmDialog
        open={isConfirmationDialog}
        title={getLabel("TXT_LOGOUT_TITLE")}
        content={getLabel("TXT_LOGOUT_TEXT")}
        submitProps={{
          submitText: getLabel("TXT_LOGOUT_BUTTON"),
          onClick: AuthUtils.handleLogout,
        }}
        cancelProps={{
          onClick: handleCloseConfirmationDialog,
        }}
      />
    </Box>
  );
};

export default memo(Sidebar);

const useStyles = makeStyles(theme => ({
  sidebarContainer: {
    height: "calc(100vh - 160px - 80px)",
    overflowY: "scroll",
    padding: 0,
  },

  sidebarItem: {
    margin: "8px 16px 8px",
    borderRadius: 4,
  },

  sidebarTitleActive: {
    fontSize: 15,
    lineHeight: "20px",
    fontWeight: 600,
    color: theme.palette.primary.main,
  },

  sidebarIcon: {
    minWidth: 36,
  },

  sidebarIconActive: {
    color: theme.palette.primary.main,
  },

  sidebarTitle: {
    fontSize: 15,
    lineHeight: "20px",
    fontWeight: 600,
    color: "#818194",
  },

  triosLogoContainer: {
    width: "100%",
    height: "160px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  triosLogo: {
    backgroundSize: "contain",
    width: "120px",
    height: "120px",
    objectFit: "contain",
  },

  triosLogoRounded: {
    borderRadius: "50%",
  },

  switchBranchBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  footer: {
    height: 80,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderTop: "1px solid #ddd",
  },
}));
